import React, { useState, useEffect } from 'react'
import moment from 'moment'
import { DateRanges, IDateRange, DateRangeQueryParamsOptions } from '../../types/date'
import { IQuery } from '../../types/search'
import { applyDateRange, getDateRange } from '../../services/date'
import { Space } from '../../layouts'
import { MultiToggle, MultiToggleButton } from '../MultiToggle'
import { CustomTextFieldOrigin } from '../CustomTextFieldOrigin'
import { StyledDatePicker } from '../CustomDateListField/StyledDatePicker'

export interface IDateFilters {
  filters: IQuery
  setFilters: (newFilters: IQuery) => void
  editorPortal?: boolean
}

export const DateFilters = ({
  filters,
  setFilters,
  editorPortal,
}: IDateFilters) => {
  const [dateRange, setDateRange] = useState(DateRanges.any)
  const theme: string = editorPortal ? 'blue' : 'orange'
  const dateFrom: string = (editorPortal && filters[DateRangeQueryParamsOptions.dateFrom])
   || filters[DateRangeQueryParamsOptions.publishedFrom] || ''
  const dateTo: string = (editorPortal && filters[DateRangeQueryParamsOptions.dateTo])
   || filters[DateRangeQueryParamsOptions.publishedTo] || ''
  const from: DateRangeQueryParamsOptions.dateFrom | DateRangeQueryParamsOptions.publishedFrom =
   editorPortal ? DateRangeQueryParamsOptions.dateFrom : DateRangeQueryParamsOptions.publishedFrom
  const to: DateRangeQueryParamsOptions.dateTo | DateRangeQueryParamsOptions.publishedTo =
   editorPortal ? DateRangeQueryParamsOptions.dateTo : DateRangeQueryParamsOptions.publishedTo

  const setDate = (date: IDateRange): void => {
    setFilters({ ...filters, ...date })
  }

  const handleDateChange = (range: DateRanges): void => {
    const todayDate = moment().format('YYYY-MM-DD')
    const nextMonth = moment()
      .add(1, 'month')
      .format('YYYY-MM-DD')
    setDate(getDateRange(range, todayDate, nextMonth, from, to))
  }

  useEffect(() => {
    setDateRange(applyDateRange(dateFrom, dateTo))
  }, [dateFrom, dateTo])

  return (
    <>
      <MultiToggle
        activeValue={dateRange}
        handleChange={(range: DateRanges): void => {
          handleDateChange(range)
        }}
        theme={theme}
      >
        <MultiToggleButton value={DateRanges.any}>Any time</MultiToggleButton>
        <MultiToggleButton value={DateRanges.on}>On date</MultiToggleButton>
        <MultiToggleButton value={DateRanges.between}>
          Between
        </MultiToggleButton>
      </MultiToggle>
      {dateRange === DateRanges.on && (
        <>
          <CustomTextFieldOrigin
            name="startDate"
            label="Date"
            value={dateFrom}
          />
          <StyledDatePicker
            theme={theme}
            open={true}
            selected={dateFrom}
            onChange={(date: string): void => {
              setDate({ [from]: date, [to]: date })
            }}
          />
        </>
      )}
      {dateRange === DateRanges.between && (
        <>
          <Space mb={3}>
            <CustomTextFieldOrigin
              name="startDate"
              label="Date From"
              value={dateFrom}
            />
            <StyledDatePicker
              showDisabledMonthNavigation
              theme={theme}
              open={true}
              maxDate={moment(dateTo).toDate()}
              selected={dateFrom}
              onChange={(date: string): void => { setDate({ [from]: date }) }}
            />
          </Space>
          <CustomTextFieldOrigin
            name="endDate"
            label="Date To"
            value={dateTo}
          />
          <StyledDatePicker
            showDisabledMonthNavigation
            theme={theme}
            open={true}
            minDate={moment(dateFrom).toDate()}
            selected={dateTo}
            onChange={(date: string): void => { setDate({ [to]: date }) }}
          />
        </>
      )}
    </>
  )
}
